import React, { useState } from 'react';
import { useTheme } from '../ThemeContext';
import { useAuth } from '../auth/AuthContext';
import styles from './Support.module.css';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';

const sectionPalettesByTheme = {
  light: {
    outstanding: {
      cardBg: '#eaf4ff',
      cardBorder: '#9ecbff',
      cardText: '#0f4c81',
      overlineText: '#356d9e',
      panelBg: '#f4faff',
      panelBorder: '#b8d8ff',
      headerBg: '#d9ecff',
      headerText: '#0f4c81',
      titleText: '#3f79ad',
      subtleText: '#3d6f9f',
    },
    dueThisMonth: {
      cardBg: '#fff8df',
      cardBorder: '#ead38b',
      cardText: '#8a6a14',
      overlineText: '#a27f20',
      panelBg: '#fffdf2',
      panelBorder: '#eddca3',
      headerBg: '#fff2c6',
      headerText: '#8a6a14',
      titleText: '#a88629',
      subtleText: '#9d7f28',
    },
    overdue: {
      cardBg: '#ffecec',
      cardBorder: '#f4aaaa',
      cardText: '#a43b3b',
      overlineText: '#bf6666',
      panelBg: '#fff6f6',
      panelBorder: '#f3c0c0',
      headerBg: '#ffdede',
      headerText: '#a43b3b',
      titleText: '#be6c6c',
      subtleText: '#b85a5a',
    },
    completed: {
      cardBg: '#edf9ef',
      cardBorder: '#b8e0be',
      cardText: '#2f7a3f',
      overlineText: '#5d9970',
      panelBg: '#f6fcf7',
      panelBorder: '#cce8d1',
      headerBg: '#ddf2e1',
      headerText: '#2f7a3f',
      titleText: '#629e6d',
      subtleText: '#4f8e5c',
    },
  },
  dark: {
    outstanding: {
      cardBg: '#132231',
      cardBorder: '#345a80',
      cardText: '#d9ecff',
      overlineText: '#b9d6f2',
      panelBg: '#101c28',
      panelBorder: '#284664',
      headerBg: '#173049',
      headerText: '#e4f2ff',
      titleText: '#bddcf6',
      subtleText: '#a9c9e8',
    },
    dueThisMonth: {
      cardBg: '#32260d',
      cardBorder: '#876a1a',
      cardText: '#ffefb6',
      overlineText: '#f7dfa1',
      panelBg: '#2a220f',
      panelBorder: '#6f5819',
      headerBg: '#3f3312',
      headerText: '#ffefb6',
      titleText: '#f2d98f',
      subtleText: '#e6d28f',
    },
    overdue: {
      cardBg: '#341819',
      cardBorder: '#8e4a4e',
      cardText: '#ffd6d6',
      overlineText: '#f0b7b7',
      panelBg: '#2c1415',
      panelBorder: '#723437',
      headerBg: '#441d20',
      headerText: '#ffdede',
      titleText: '#f0bcbc',
      subtleText: '#ebb1b1',
    },
    completed: {
      cardBg: '#163121',
      cardBorder: '#3f7b53',
      cardText: '#d8f2de',
      overlineText: '#b9dfc3',
      panelBg: '#13291c',
      panelBorder: '#356746',
      headerBg: '#1d3c28',
      headerText: '#dff6e4',
      titleText: '#bfe2c6',
      subtleText: '#add0b4',
    },
  },
};

  

const Support = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const sectionPalettes = sectionPalettesByTheme[theme] || sectionPalettesByTheme.light;
  const userFirstName = (user?.name || user?.email || 'there')
    .split(/[ @]/)
    .find(Boolean) || 'there';

    const [testMessage, setTestMessage] = useState('');
    const [sendingReminderTest, setSendingReminderTest] = useState(false);
    const [sendingMonthlyTest, setSendingMonthlyTest] = useState(false);
    const [summary, setSummary] = useState(null);
    const [loadingSummary, setLoadingSummary] = useState(true);
    const [summaryError, setSummaryError] = useState('');

    React.useEffect(() => {
      let ignore = false;

      const loadSummary = async () => {
        setLoadingSummary(true);
        setSummaryError('');
        try {
          const response = await fetch('/api/user/summary', {
            credentials: 'same-origin',
          });
          const payload = await response.json().catch(() => ({}));
          if (!response.ok) {
            throw new Error(payload.message || 'Unable to load your task summary.');
          }
          if (!ignore) {
            setSummary(payload);
          }
        } catch (err) {
          if (!ignore) {
            setSummary(null);
            setSummaryError(err.message || 'Unable to load your task summary.');
          }
        } finally {
          if (!ignore) {
            setLoadingSummary(false);
          }
        }
      };

      loadSummary();
      return () => {
        ignore = true;
      };
    }, []);

    const runEmailTest = async (path, setLoadingState) => {
      setLoadingState(true);
      setTestMessage('');
      try {
        const response = await fetch(path, {
          method: 'POST',
          credentials: 'same-origin',
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload.message || 'Unable to send test email.');
        }
        setTestMessage(payload.message || 'Test email request completed.');
        if (path === '/api/support/test-monthly-report') {
          setSummary((current) => current ? { ...current } : current);
        }
      } catch (err) {
        setTestMessage(err.message || 'Unable to send test email.');
      } finally {
        setLoadingState(false);
      }
    };

    const renderGroupedRows = (title, rows, dateField, palette) => (
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          backgroundColor: palette.panelBg,
          border: `1px solid ${palette.panelBorder}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            backgroundColor: palette.headerBg,
            borderBottom: `1px solid ${palette.panelBorder}`,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'DM Serif Display, serif',
              fontWeight: 400,
              color: palette.titleText,
            }}
          >
            {title}
          </Typography>
        </Box>
        <Box sx={{ px: 2, py: 2 }}>
          {!rows?.length ? (
            <Typography variant="body2" sx={{ color: palette.subtleText }}>
              No items.
            </Typography>
          ) : (
            Object.entries(rows.reduce((acc, row) => {
              const key = `${row.CompanyName} / ${row.DivisionName}`;
              if (!acc[key]) acc[key] = [];
              acc[key].push(row);
              return acc;
            }, {})).map(([groupName, items]) => (
              <Box key={groupName} sx={{ mb: 2.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: palette.headerText }}>
                  {groupName}
                </Typography>
                {items.map((item) => (
                  <Box key={`${groupName}-${item.ActionID}`} sx={{ py: 0.75 }}>
                    <Typography variant="body1" sx={{ color: palette.headerText }}>
                      {item.ActionName}
                    </Typography>
                    <Typography variant="body2" sx={{ color: palette.subtleText }}>
                      {dateField}: {item[dateField] ? new Date(item[dateField]).toDateString() : '-'}
                    </Typography>
                  </Box>
                ))}
                <Divider sx={{ mt: 1.5, borderColor: palette.panelBorder }} />
              </Box>
            ))
          )}
        </Box>
      </Paper>
    );

  return (
  <>
      <aside className={`${styles.support} ${styles[theme]}`}>
            
              
            <Box className={`  ${styles[theme]}`} sx={{display: 'flex',flexDirection: 'row',width: '100%'}}>
               {/* BOX1 */}
              <Box className={` ${styles[theme]}`} sx={{m:1,width:'100%'}}>
                <Paper elevation={1} className={styles[theme]}> 
                
                <Box className={`${styles.box} ${styles[theme]}`}
                    sx={{
                      display: 'flex', // Use flex display
                      justifyContent: 'space-between', // Center the content horizontally
                      alignItems: 'center', // Align items vertically in the center
                      paddingTop: '8px', // Optional padding at the top
                      paddingBottom: '8px', // Optional padding at the bottom
                    }}
                  >
                    

                    {/* Typography for "Actions" text */}
                    <Box sx={{
                      display: 'flex',
                       }}>
                    <Typography 
                      sx={{
                        fontFamily: 'DM Serif Display, serif',
                        fontWeight: '400',
                        fontStyle: 'normal',
                        textAlign: 'left',
                        flexGrow: 1, // Allows the text to grow and push the icon to the right
                        paddingLeft: '10px'
                      }}
                      variant="h6"
                      gutterBottom
                    >
                     {`Hi ${userFirstName}`}
                     <Typography variant="body2">
                    This page displays your outstanding tasks, monthly report, and overall summary in one place.
                  </Typography>
                     
                    </Typography>
                    
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, paddingRight: '10px' }}>
                      {/* <Button
                        variant="outlined"
                        size="small"
                        onClick={() => runEmailTest('/api/support/test-reminders', setSendingReminderTest)}
                        disabled={sendingReminderTest || sendingMonthlyTest}
                      >
                        {sendingReminderTest ? 'Sending...' : 'Email me all due tasks'}
                      </Button> */}
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => runEmailTest('/api/support/test-monthly-report', setSendingMonthlyTest)}
                        disabled={sendingReminderTest || sendingMonthlyTest}
                      >
                        {sendingMonthlyTest ? 'Sending...' : 'Email me monthly report'}
                      </Button>
                    </Box>
                     {/* IconButton wrapping the IoMdAddCircle icon */}
                    {/* <Button onClick={handleClickOpen} aria-label="add">
                      <MdOutlineLibraryAdd style={{ fontSize: '30px', float: 'right' }} />
                    </Button> */}
                    
                   


                 


                  </Box>
               
                </Paper>
                {testMessage ? (
                  <Box sx={{ px: 2, py: 1 }}>
                    <Typography variant="body2" sx={{ color: theme === 'dark' ? '#d8d8d8' : '#1f2937' }}>{testMessage}</Typography>
                  </Box>
                ) : null}
                <Box sx={{ px: 2, py: 2 }}>
                  
                  
                </Box>
                <Box sx={{ px: 2, pb: 2 }}>
                  {loadingSummary ? (
                    <Typography variant="body2" sx={{ color: theme === 'dark' ? '#d8d8d8' : '#1f2937' }}>Loading your task summary...</Typography>
                  ) : summaryError ? (
                    <Typography variant="body2" sx={{ color: theme === 'dark' ? '#f2c4c4' : '#8b1e1e' }}>{summaryError}</Typography>
                  ) : summary ? (
                    <>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6} lg={3}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              backgroundColor: sectionPalettes.outstanding.cardBg,
                              border: `1px solid ${sectionPalettes.outstanding.cardBorder}`,
                            }}
                          >
                            <Typography variant="overline" sx={{ color: sectionPalettes.outstanding.overlineText }}>
                              Total Tasks
                            </Typography>
                            <Typography variant="h4" sx={{ color: sectionPalettes.outstanding.cardText }}>
                              {summary.counts.pending}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              backgroundColor: sectionPalettes.overdue.cardBg,
                              border: `1px solid ${sectionPalettes.overdue.cardBorder}`,
                            }}
                          >
                            <Typography variant="overline" sx={{ color: sectionPalettes.overdue.overlineText }}>
                              Overdue
                            </Typography>
                            <Typography variant="h4" sx={{ color: sectionPalettes.overdue.cardText }}>
                              {summary.counts.overdue}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              backgroundColor: sectionPalettes.dueThisMonth.cardBg,
                              border: `1px solid ${sectionPalettes.dueThisMonth.cardBorder}`,
                            }}
                          >
                            <Typography variant="overline" sx={{ color: sectionPalettes.dueThisMonth.overlineText }}>
                              Due In 30 Days
                            </Typography>
                            <Typography variant="h4" sx={{ color: sectionPalettes.dueThisMonth.cardText }}>
                              {summary.counts.dueNext30Days}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              backgroundColor: sectionPalettes.completed.cardBg,
                              border: `1px solid ${sectionPalettes.completed.cardBorder}`,
                            }}
                          >
                            <Typography variant="overline" sx={{ color: sectionPalettes.completed.overlineText }}>
                              Completed In 30 Days
                            </Typography>
                            <Typography variant="h4" sx={{ color: sectionPalettes.completed.cardText }}>
                              {summary.counts.completedLast30Days}
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                      {renderGroupedRows('Overdue tasks', summary.sections.overdue, 'DueDate', sectionPalettes.overdue)}
                      {renderGroupedRows(summary.labels.dueNext30Days || 'Tasks due in the next 30 days', summary.sections.dueNext30Days, 'DueDate', sectionPalettes.dueThisMonth)}
                      {renderGroupedRows(summary.labels.completedLast30Days || 'Completed in the last 30 days', summary.sections.completedLast30Days, 'CompletedDate', sectionPalettes.completed)}
                      {renderGroupedRows(summary.labels.remainingThisYear || 'Remaining tasks for this year', summary.sections.remainingThisYear, 'DueDate', sectionPalettes.outstanding)}
                    </>
                  ) : null}
                </Box>
              </Box>
            </Box>   
      </aside>
      
  </>
  )
}

export default Support
